/**
 * AS ABAS DA CONFIGURAÇÃO, ALCANÇÁVEIS PELO TECLADO.
 *
 * O QUE FOI MEDIDO. As oito abas de cima ("Minha Conta", "Lojas", "Cardápio"…)
 * e as de lado ("Dados Pessoais", "Segurança e Privacidade"…) são `<div>` com
 * `onClick`. Lido no navegador:
 *
 *     tag DIV · tabIndex -1 · role null · aria-selected null
 *     elementos focáveis na faixa inteira: 0   (de 8 abas)
 *
 * Um `<div onClick>` não recebe foco, não entra na ordem do Tab e não responde
 * a Enter. Na prática: quem navega por teclado NÃO CONSEGUE TROCAR DE ABA —
 * a Configuração inteira, que é onde se cadastra loja, cardápio, SKU e
 * impressora, era acessível só com mouse. E o leitor de tela anunciava oito
 * pedaços de texto solto, sem dizer que são abas nem qual está aberta.
 *
 * O QUE ESTE HOOK FAZ — o padrão de abas do WAI-ARIA, que é o mesmo que o
 * FlyOps ganha de graça por usar Radix:
 *
 *  1. PAPÉIS. A faixa vira `role="tablist"`, cada item vira `role="tab"` com
 *     `aria-selected`. É o que faz o leitor anunciar "aba 3 de 8, selecionada".
 *  2. FOCO ITINERANTE (roving tabindex). Só a aba SELECIONADA tem `tabIndex 0`;
 *     as outras têm -1. Assim o Tab entra na faixa e SAI dela com um toque, em
 *     vez de obrigar a pessoa a passar por oito paradas para chegar ao
 *     conteúdo. Quem anda entre as abas é a seta.
 *  3. SETAS, HOME E END. Esquerda/direita (ou cima/baixo, na faixa vertical)
 *     movem e JÁ ATIVAM a aba — é o comportamento de "seleção automática" do
 *     padrão, o mesmo do Radix. Home vai à primeira, End à última.
 *  4. ENTER E ESPAÇO ativam, porque um `role="tab"` que não responde a Enter é
 *     uma promessa quebrada para quem usa leitor de tela.
 *  5. A ABA ATIVA ENTRA NA VISTA. No telefone a faixa de cima rola de lado com
 *     a barra de rolagem ESCONDIDA (`::-webkit-scrollbar { display: none }`) —
 *     medido em 375px: 838px de abas em 318px visíveis, cinco delas fora da
 *     tela. Sem este empurrão, abrir `/settings?aba=Avançado` mostraria a faixa
 *     parada no começo, com a aba aberta invisível.
 *
 * POR QUE UM HOOK E NÃO TROCAR OS `div` POR `button`. Trocar a tag é o certo a
 * longo prazo, mas `HorizontalOptionItem` e `VerticalOptionItem` carregam ~40
 * linhas de estilo cada uma (tamanho único, sublinhado, scroll-snap) que o
 * reset de botão desmancharia — e a trava do gabarito visual exige diferença
 * zero em 1440. Com `tabIndex`, `role` e os manipuladores de tecla, um `div`
 * atende ao padrão sem repintar nada.
 */
import { useCallback, useEffect, useRef } from "react";

interface Opcoes<T extends string> {
  /** As abas, na ordem em que aparecem. */
  itens: T[];
  /** A que está aberta. */
  selecionada: T;
  /** Chamado ao ativar — por clique, seta, Enter ou Espaço. */
  aoSelecionar: (item: T) => void;
  /**
   * A direção da faixa. Decide QUAIS setas andam: a horizontal usa
   * esquerda/direita, a vertical usa cima/baixo. Usar as duas em ambas
   * atrapalha — na faixa vertical, seta-baixo tem que continuar rolando a
   * página quando o foco não está nas abas.
   */
  orientacao?: "horizontal" | "vertical";
  /** Nome da faixa para o leitor de tela. Ex.: "Seções da configuração". */
  rotulo: string;
}

export function useAbasAcessiveis<T extends string>({
  itens,
  selecionada,
  aoSelecionar,
  orientacao = "horizontal",
  rotulo,
}: Opcoes<T>) {
  const refDaFaixa = useRef<HTMLDivElement | null>(null);

  /*
   * A ABA ATIVA ENTRA NA VISTA — e com `block: "nearest"`, que é o detalhe que
   * evita o efeito colateral.
   *
   * `scrollIntoView` sem opções rola TODOS os ancestrais roláveis, inclusive a
   * página: abrir uma aba jogaria a janela inteira para o meio do documento.
   * Com `nearest` no eixo vertical, o empurrão fica no eixo que interessa.
   *
   * `setTimeout` e não `requestAnimationFrame` pelo mesmo motivo registrado em
   * `useModalAcessivel`: em aba oculta o navegador não emite quadros, e o
   * empurrão nunca aconteceria.
   */
  useEffect(() => {
    const id = setTimeout(() => {
      const faixa = refDaFaixa.current;
      if (!faixa) return;
      const ativa = faixa.querySelector<HTMLElement>('[aria-selected="true"]');
      if (!ativa) return;
      // Só empurra se ela estiver mesmo fora — senão a faixa "pula" a cada
      // render sem motivo.
      const caixaDaFaixa = faixa.getBoundingClientRect();
      const caixaDaAba = ativa.getBoundingClientRect();
      const fora =
        caixaDaAba.left < caixaDaFaixa.left ||
        caixaDaAba.right > caixaDaFaixa.right;
      if (!fora) return;
      ativa.scrollIntoView({ block: "nearest", inline: "center" });
    }, 0);
    return () => clearTimeout(id);
  }, [selecionada]);

  const aoTeclar = useCallback(
    (evento: React.KeyboardEvent, item: T) => {
      const anterior = orientacao === "horizontal" ? "ArrowLeft" : "ArrowUp";
      const proxima = orientacao === "horizontal" ? "ArrowRight" : "ArrowDown";
      const indice = itens.indexOf(item);
      if (indice < 0) return;

      let destino: number | null = null;
      if (evento.key === anterior) destino = (indice - 1 + itens.length) % itens.length;
      else if (evento.key === proxima) destino = (indice + 1) % itens.length;
      else if (evento.key === "Home") destino = 0;
      else if (evento.key === "End") destino = itens.length - 1;
      else if (evento.key === "Enter" || evento.key === " ") {
        // Espaço rolaria a página se não fosse barrado — e a pessoa perderia o
        // lugar exatamente ao tentar abrir uma aba.
        evento.preventDefault();
        aoSelecionar(item);
        return;
      } else return;

      evento.preventDefault();
      const alvo = itens[destino];
      aoSelecionar(alvo);
      // O foco acompanha a seleção: sem isto a seta seguinte partiria da aba
      // ANTIGA, e a navegação andaria de um em um sem sair do lugar.
      setTimeout(() => {
        refDaFaixa.current
          ?.querySelector<HTMLElement>('[aria-selected="true"]')
          ?.focus();
      }, 0);
    },
    [itens, orientacao, aoSelecionar],
  );

  /** Vai no elemento da FAIXA. */
  const propsDaFaixa = {
    ref: refDaFaixa,
    role: "tablist" as const,
    "aria-label": rotulo,
    "aria-orientation": orientacao,
  };

  /** Vai em CADA aba. */
  const propsDaAba = (item: T) => ({
    role: "tab" as const,
    "aria-selected": item === selecionada,
    // Roving tabindex: uma parada de Tab para a faixa inteira.
    tabIndex: item === selecionada ? 0 : -1,
    onKeyDown: (evento: React.KeyboardEvent) => aoTeclar(evento, item),
  });

  return { propsDaFaixa, propsDaAba };
}
